import { useMemo } from "react";
import { css } from "styled-system/css";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  type ChartOptions,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { formatUsd } from "@/lib/format-usd";
import { formatMonthLabel } from "@/lib/month-range";
import { ChartCard, EmptyChart } from "./admin-charts";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

const LINE_PALETTE = [
  "rgb(99, 102, 241)",
  "rgb(236, 72, 153)",
  "rgb(16, 185, 129)",
  "rgb(245, 158, 11)",
  "rgb(59, 130, 246)",
  "rgb(139, 92, 246)",
  "rgb(239, 68, 68)",
  "rgb(20, 184, 166)",
  "rgb(217, 70, 239)",
  "rgb(101, 163, 13)",
];

const PIE_PALETTE = [
  "rgba(99, 102, 241, 0.85)",
  "rgba(236, 72, 153, 0.8)",
  "rgba(16, 185, 129, 0.8)",
  "rgba(245, 158, 11, 0.8)",
  "rgba(59, 130, 246, 0.8)",
  "rgba(139, 92, 246, 0.8)",
  "rgba(239, 68, 68, 0.8)",
  "rgba(20, 184, 166, 0.8)",
  "rgba(148, 163, 184, 0.8)",
];

const tooltipBase = {
  backgroundColor: "#0f172a",
  padding: 10,
  cornerRadius: 8,
  titleFont: { size: 12, weight: "bold" as const },
  bodyFont: { size: 12 },
};

export type AdminCostChartsProps = {
  monthLabels: string[];
  waCostByMonth: number[];
  emailCostByMonth: number[];
  accountSeries: { slug: string; dataByMonth: number[] }[];
  pieLabels: string[];
  pieData: number[];
};

export function AdminCostCharts({ monthLabels, waCostByMonth, emailCostByMonth, accountSeries, pieLabels, pieData }: AdminCostChartsProps) {
  const monthDisplayLabels = useMemo(() => monthLabels.map(formatMonthLabel), [monthLabels]);

  const stackedEmpty = waCostByMonth.every((v) => v === 0) && emailCostByMonth.every((v) => v === 0);
  const linesEmpty = accountSeries.length === 0 || accountSeries.every((s) => s.dataByMonth.every((v) => v === 0));
  const pieEmpty = pieData.length === 0 || pieData.every((v) => v === 0);

  const barOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { font: { size: 11 }, color: "#64748b", boxWidth: 10, boxHeight: 10 } },
      tooltip: {
        ...tooltipBase,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${formatUsd((ctx.parsed.y as number) ?? 0)}`,
          footer: (items) => `Total: ${formatUsd(items.reduce((sum, it) => sum + ((it.parsed.y as number) ?? 0), 0))}`,
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: "#94a3b8", font: { size: 11 } }, border: { display: false } },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: "rgba(226, 232, 240, 0.7)" },
        ticks: { color: "#94a3b8", font: { size: 11 }, callback: (v) => `$${Number(v).toFixed(0)}` },
        border: { display: false },
      },
    },
  };

  const lineOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "nearest", intersect: false },
    plugins: {
      legend: { position: "bottom", labels: { font: { size: 11 }, color: "#64748b", boxWidth: 10, boxHeight: 10 } },
      tooltip: {
        ...tooltipBase,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${formatUsd((ctx.parsed.y as number) ?? 0)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#94a3b8", font: { size: 11 } }, border: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(226, 232, 240, 0.7)" },
        ticks: { color: "#94a3b8", font: { size: 11 }, callback: (v) => `$${Number(v).toFixed(0)}` },
        border: { display: false },
      },
    },
  };

  const pieTotal = pieData.reduce((a, b) => a + b, 0);
  const doughOptions: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: {
      legend: { position: "bottom", labels: { font: { size: 11 }, color: "#64748b", padding: 10, boxWidth: 10, boxHeight: 10 } },
      tooltip: {
        ...tooltipBase,
        callbacks: {
          label: (ctx) => {
            const value = (ctx.parsed as number) ?? 0;
            const pct = pieTotal > 0 ? ((value / pieTotal) * 100).toFixed(1) : "0.0";
            return ` ${ctx.label}: ${formatUsd(value)} (${pct}%)`;
          },
        },
      },
    },
  };

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "4", mb: "6" })}>
      <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", lg: "repeat(2, 1fr)" }, gap: "4" })}>
        <ChartCard title="Gasto mensual total (WA vs. Email)">
          {stackedEmpty ? (
            <EmptyChart label="Sin envíos en el rango seleccionado." />
          ) : (
            <div className={css({ h: "260px", flex: "1" })}>
              <Bar
                data={{
                  labels: monthDisplayLabels,
                  datasets: [
                    { label: "WhatsApp", data: waCostByMonth, backgroundColor: "rgba(99, 102, 241, 0.82)", stack: "costs", borderRadius: 4, maxBarThickness: 40 },
                    { label: "Email", data: emailCostByMonth, backgroundColor: "rgba(148, 163, 184, 0.75)", stack: "costs", borderRadius: 4, maxBarThickness: 40 },
                  ],
                }}
                options={barOptions}
              />
            </div>
          )}
        </ChartCard>

        <ChartCard title="Distribución de costo por cuenta">
          {pieEmpty ? (
            <EmptyChart label="Sin costos en el rango seleccionado." />
          ) : (
            <div className={css({ h: "260px", flex: "1" })}>
              <Doughnut
                data={{
                  labels: pieLabels,
                  datasets: [{ data: pieData, backgroundColor: PIE_PALETTE, borderWidth: 0, hoverOffset: 6 }],
                }}
                options={doughOptions}
              />
            </div>
          )}
        </ChartCard>
      </div>

      <ChartCard title="Gasto por cuenta a lo largo del tiempo" fullWidth>
        {linesEmpty ? (
          <EmptyChart label="Sin costos en el rango seleccionado." />
        ) : (
          <div className={css({ h: "280px", flex: "1" })}>
            <Line
              data={{
                labels: monthDisplayLabels,
                datasets: accountSeries.map((s, i) => {
                  const color = LINE_PALETTE[i % LINE_PALETTE.length];
                  return {
                    label: s.slug,
                    data: s.dataByMonth,
                    borderColor: color,
                    backgroundColor: color,
                    borderWidth: 2,
                    pointRadius: 3,
                    pointBackgroundColor: color,
                    tension: 0.3,
                  };
                }),
              }}
              options={lineOptions}
            />
          </div>
        )}
      </ChartCard>
    </div>
  );
}
